import { FMSCapabilityState, FMSObjectType } from "../FMS/FMS.Capabilities";
import { FMSType } from "../FMS/FMS.Interfaces";
import { BotSequenceType } from "./BotSequence.Enums";

/**
 * Unit of a stored JIT freshness window. The window is kept EXACTLY as the admin entered
 * it (amount + unit, never a canonical minutes scalar) — a critical setting must be echoed
 * back in the unit it was typed in, and ops scripts write a natural (amount, unit) pair.
 * Conversion to minutes happens in exactly one place: BotSequenceJitConfigService.
 */
export enum BotSequenceJitFreshnessUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days"
}

/**
 * One stored JIT cell — the value `fms.jitSettings` holds per sequence type. A cell that
 * is all defaults (enabled, no window) is never stored — it is removed instead, so the
 * type falls back to the defaults (enabled, the global freshness window).
 */
export interface BotSequenceJitSettingValue {
  /** The window exactly as entered; NULL = global default. Set iff freshnessUnit is set. */
  freshnessAmount: number | null;
  /** The unit exactly as entered; set iff freshnessAmount is set. */
  freshnessUnit: BotSequenceJitFreshnessUnit | null;
  /** Explicit JIT on/off for this cell (new engine only). Default true. */
  enabled: boolean;
}

/**
 * The JIT settings of ONE FMS integration (ENG-3213): per sequence type, the explicit JIT
 * switch and the freshness window. This is the shape of the nullable `fms.jitSettings`
 * jsonb column — the integration row supplies the FMS type (and therefore the capability
 * gate) and the org. No key for a type = the defaults (enabled, the global window from
 * BOT_SEQUENCE_JIT_FRESHNESS_MINUTES). Cells whose jitFetch capability is not
 * Implemented for the integration's FMS type are never stored.
 */
export type FMSJitSettings = Partial<Record<BotSequenceType, BotSequenceJitSettingValue>>;

/**
 * The object each sequence type's JIT refreshes — what the jitFetch capability gate is
 * checked against (Lease for the lease-anchored types, Lead for the cart). Shared by the
 * settings API (capability derivation), the config read path, and the studio UI.
 */
export const BOT_SEQUENCE_JIT_OBJECT: Partial<Record<BotSequenceType, FMSObjectType>> = {
  [BotSequenceType.PAST_DUE]: FMSObjectType.Lease,
  [BotSequenceType.ABANDONED_CART]: FMSObjectType.Lead,
  [BotSequenceType.MOVE_IN]: FMSObjectType.Lease,
  [BotSequenceType.MOVE_OUT]: FMSObjectType.Lease
};

/** jitFetch capability state per sequence type for one FMS type (derived via BOT_SEQUENCE_JIT_OBJECT). */
export type FMSJitCapabilities = Partial<Record<BotSequenceType, FMSCapabilityState>>;

/** One payload for the FMS integration page's "JIT freshness" section. */
export interface FMSJitSettingsResponse {
  fmsId: number;
  fmsType: FMSType;
  /** The global window (BOT_SEQUENCE_JIT_FRESHNESS_MINUTES), applied wherever nothing custom is set. */
  defaultMinutes: number;
  /** The cells stored on the integration — only types with a custom value have a key. */
  settings: FMSJitSettings;
  /** The jitFetch state per sequence type for the integration's FMS type — the UI disables the rest. */
  capabilities: FMSJitCapabilities;
}

/**
 * PUT body — MERGE semantics per sequence type: a type present in `settings` has its cell
 * replaced whole, an omitted type keeps its current cell. An all-defaults cell (enabled,
 * no window) removes the type's key. Types whose jitFetch capability is not Implemented
 * are rejected.
 */
export interface FMSJitSettingsUpdateRequest {
  settings: FMSJitSettings;
}
